/**
 * Global Event Handlers
 * Centralized event handling for application-wide events
 */

import appState from '../core/app-state.js';
import { AUTO_ADVANCE_ENABLED } from '../config/feature-flags.js';
import * as QuestionManager from '../modules/questions/question-manager.js';

/**
 * Setup auto-advance cancellation listeners
 * Registers global event listeners that cancel auto-advance on user interaction
 */
export function setupAutoAdvanceCancellation(): void {
  if (!AUTO_ADVANCE_ENABLED) return;

  // Track if we've seen the first pointermove to avoid cancelling on initial load
  let hasSeenPointerMove = false;

  // Cancel on any key press
  document.addEventListener('keydown', () => {
    if (appState.autoAdvanceActive) {
      QuestionManager.cancelAutoAdvance();
    }
  });

  // Cancel on ANY click
  document.addEventListener('click', () => {
    if (appState.autoAdvanceActive) {
      QuestionManager.cancelAutoAdvance();
    }
  }, { passive: true });

  // Cancel on first pointer movement (mouse/touch)
  document.addEventListener('pointermove', () => {
    if (!hasSeenPointerMove) {
      hasSeenPointerMove = true;
      return; // Don't cancel on first movement (page load)
    }
    if (appState.autoAdvanceActive) {
      QuestionManager.cancelAutoAdvance();
    }
  }, { passive: true });

  // Cancel on focus changes
  document.addEventListener('focusin', () => {
    if (appState.autoAdvanceActive) {
      QuestionManager.cancelAutoAdvance();
    }
  }, { passive: true });

  document.addEventListener('focusout', () => {
    if (appState.autoAdvanceActive) {
      QuestionManager.cancelAutoAdvance();
    }
  }, { passive: true });
}

/**
 * Setup mobile menu event handlers
 * Handles opening, closing, and click-outside behavior for mobile menu
 */
export function setupMobileMenuHandlers(): void {
  const mobileMenuBtn = document.getElementById('mobile-menu-btn');
  const headerActions = document.getElementById('header-actions');
  
  if (!mobileMenuBtn || !headerActions) return;

  mobileMenuBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    const isOpen = headerActions.classList.toggle('mobile-menu-open');
    mobileMenuBtn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  });
  
  // Close mobile menu when clicking outside
  document.addEventListener('click', (e) => {
    if (window.innerWidth < 900) { // Only on mobile
      const target = e.target as Node;
      if (!headerActions.contains(target) && !mobileMenuBtn.contains(target)) {
        if (headerActions.classList.contains('mobile-menu-open')) {
          headerActions.classList.remove('mobile-menu-open');
          mobileMenuBtn.setAttribute('aria-expanded', 'false');
        }
      }
    }
  });
  
  // Close mobile menu when a menu item is clicked
  headerActions.addEventListener('click', (e) => {
    if (window.innerWidth < 900 && (e.target as Element).closest('button')) {
      headerActions.classList.remove('mobile-menu-open');
      mobileMenuBtn.setAttribute('aria-expanded', 'false');
    }
  });
}
